import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { useAuth } from '@/contexts/AuthContext';

const navLinks = [
  { label: '首页', path: '/' },
  { label: '教学视频', path: '/videos' },
  { label: '互动交流', path: '/community' },
  { label: '商店', path: '/shop' },
  { label: '关于我们', path: '/about' },
  { label: '联系我们', path: '/contact' },
];

const donateLink = { label: '❤️ 婴儿公益', path: '/donate' };

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { totalCount } = useCart();
  const { user, profile, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const avatarLetter = profile?.name ? profile.name[0].toUpperCase() : (user?.email?.[0]?.toUpperCase() ?? 'U');

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
            alt="金榜旺财Logo"
            className="w-10 h-10 rounded-full object-cover"
          />
          <div className="flex flex-col leading-tight">
            <span className={`font-bold text-base tracking-wide ${scrolled ? 'text-red-700' : 'text-white'}`}>
              金榜旺财
            </span>
            <span className={`text-xs ${scrolled ? 'text-gray-500' : 'text-white/80'}`}>
              高考·考研升学平台
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                location.pathname === link.path
                  ? scrolled
                    ? 'bg-red-600 text-white'
                    : 'bg-white/20 text-white'
                  : scrolled
                  ? 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                  : 'text-white/90 hover:text-white hover:bg-white/15'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA Button */}
        <div className="hidden md:flex items-center gap-3">
          {/* Donate Button */}
          <Link
            to="/donate"
            className="px-4 py-2 rounded-full text-sm font-bold bg-rose-500 text-white hover:bg-rose-400 transition-all whitespace-nowrap cursor-pointer"
          >
            ❤️ 婴儿公益
          </Link>

          {/* Cart Icon */}
          <Link to="/cart" className={`relative w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer ${scrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/15'}`}>
            <i className="ri-shopping-cart-line text-lg"></i>
            {totalCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-600 text-white text-xs rounded-full flex items-center justify-center font-bold leading-none">
                {totalCount > 9 ? '9+' : totalCount}
              </span>
            )}
          </Link>

          {user ? (
            <div className="flex items-center gap-2">
              <Link to="/profile" className="flex items-center gap-2 cursor-pointer group">
                <div className="w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">{avatarLetter}</span>
                </div>
                <span className={`text-sm font-medium whitespace-nowrap ${scrolled ? 'text-gray-700 group-hover:text-red-600' : 'text-white/90 group-hover:text-white'}`}>
                  {profile?.name || '我的主页'}
                </span>
              </Link>
              <button
                onClick={() => signOut()}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-500 hover:text-red-600 hover:bg-red-50' : 'text-white/70 hover:text-white hover:bg-white/15'
                }`}
              >
                退出
              </button>
            </div>
          ) : (
            <>
              <Link to="/login" className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer border ${
                scrolled
                  ? 'border-red-600 text-red-600 hover:bg-red-600 hover:text-white'
                  : 'border-white text-white hover:bg-white hover:text-red-700'
              }`}>
                登录
              </Link>
              <Link to="/login" className="px-5 py-2 rounded-full text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer">
                免费注册
              </Link>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 flex flex-col gap-2">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-3 rounded-lg text-sm font-medium cursor-pointer ${
                location.pathname === link.path
                  ? 'bg-red-50 text-red-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link to="/donate" className="w-full py-2.5 rounded-full bg-rose-500 text-white text-sm font-bold cursor-pointer text-center whitespace-nowrap mt-1">
            ❤️ 婴儿公益捐款
          </Link>
          {user ? (
            <div className="flex gap-2 mt-2">
              <Link to="/profile" className="flex-1 py-2 rounded-full bg-red-600 text-white text-sm font-semibold cursor-pointer text-center whitespace-nowrap">
                我的主页
              </Link>
              <button onClick={() => signOut()} className="flex-1 py-2 rounded-full border border-gray-300 text-gray-600 text-sm font-semibold cursor-pointer whitespace-nowrap">
                退出登录
              </button>
            </div>
          ) : (
            <div className="flex gap-2 mt-2">
              <Link to="/login" className="flex-1 py-2 rounded-full border border-red-600 text-red-600 text-sm font-semibold cursor-pointer text-center whitespace-nowrap">登录</Link>
              <Link to="/login" className="flex-1 py-2 rounded-full bg-red-600 text-white text-sm font-semibold cursor-pointer text-center whitespace-nowrap">免费注册</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
